import { Link } from 'react-router-dom'

import SquarePen from '@/assets/note-pencil.svg?react'
import { useApi } from '@/hooks/useApi'
import { useInlineRename } from '@/hooks/useInlineRename'
import { getModifierKey } from '@/hooks/useKeyboardShortcut'
import { useOS } from '@/hooks/utils/useOS'
import { cn } from '@/lib/utils'
import { useAllConversationsStore } from '@/store/useAllConversationsStore'
import { useCurrentConversationStore } from '@/store/useCurrentConversationStore'
import { useUIStore } from '@/store/useUIStore'
import { useItemColors } from '@/utils/useItemColors'

import { ConversationItemDropdown } from './ConversationItemDropdown'

export const ConversationItemSkeleton = () => {
  const width = 10 + Math.floor(Math.random() * 6) + 'em'
  return (
    <div className="relative">
      <div className="relative mx-[6px] flex min-h-9 items-center justify-between gap-2 rounded-md px-2 py-[10px]">
        <div className="flex min-w-0 grow items-center gap-1 text-sm new-chat:text-white/90 dark:text-white/90 dark:new-chat:text-white/90 new-chat:dark:text-white/90">
          <div className="flex min-w-0 grow items-center gap-2">
            <div
              className="h-4 animate-pulse rounded bg-black/5 dark:bg-white/25"
              style={{ width }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export const NewConversationItem = () => {
  const classes = useItemColors({ isActive: false })
  const closeSidebarIfMobile = useUIStore(s => s.closeSidebarIfMobile)
  const isNewChat = useCurrentConversationStore(state => state.id) === null
  const isStreaming = useCurrentConversationStore(
    s => s.streamPhase === 'loading' || s.streamPhase === 'streaming',
  )
  const os = useOS()
  const metaKey = getModifierKey(os).meta
  return (
    <div className="group relative">
      <div
        className={cn(
          classes,
          'relative mx-[6px] flex min-h-9 items-center justify-between gap-2 rounded-md px-2 py-[10px] text-sm',
        )}
      >
        <Link
          to={isStreaming ? '#' : '/chat'}
          onClick={closeSidebarIfMobile}
          className={cn(
            isNewChat && 'text-black/40 new-chat:dark:text-white/40',
            'flex min-w-0 grow items-center gap-1 text-sm new-chat:text-white/90 dark:text-white/90',
            isStreaming && 'pointer-events-none opacity-50',
          )}
          aria-label={'New Chat'}
          tabIndex={0}
        >
          <div className="flex min-w-0 grow items-center gap-2 new-chat:text-white">
            <div className="flex size-[18px] shrink-0 items-center justify-center new-chat:text-white">
              <SquarePen className="new-chat:text-white" />
            </div>
            <div className="truncate">New Chat</div>
            <dd className="absolute right-0 isolate hidden items-center self-stretch justify-self-end whitespace-nowrap pe-4 text-muted-foreground opacity-0 transition-all delay-1000 duration-1000 md:block group-hover:md:opacity-100">
              <div className="inline-flex whitespace-pre *:inline-flex *:font-sans">
                <kbd aria-label="Shift">
                  <span className="min-w-[1em]">⇧</span>
                </kbd>
                <kbd aria-label="Command">
                  <span className="min-w-[1em]">{metaKey}</span>
                </kbd>
                <kbd>
                  <span className="min-w-[1em]">O</span>
                </kbd>
              </div>
            </dd>
            {/* <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono font-medium text-muted-foreground opacity-100 text-xs">
              <span className="text-xs">⌘</span>
              <span className="text-xs">⇧</span>O
            </kbd> */}
          </div>
        </Link>
      </div>
    </div>
  )
}

/**
 * ConversationItem – a single conversation item in the sidebar.
 *
 * @param {string} title  The title of the conversation
 * @param {string} to  The URL to navigate to when the conversation is clicked
 * @param {string} convId  The ID of the conversation
 */
export const ConversationItem = ({ title, to, convId }) => {
  const closeSidebarIfMobile = useUIStore(s => s.closeSidebarIfMobile)
  const editingId = useAllConversationsStore(s => s.editingTitleId)
  const setEditingId = useAllConversationsStore(s => s.setEditingTitleId)
  const updateConversation = useAllConversationsStore(s => s.updateConversation)
  const api = useApi()
  const currentId = useCurrentConversationStore(state => state.id)
  const isActive = currentId === convId
  const classes = useItemColors({ isActive })
  const isEditing = editingId === convId
  const { text, props } = useInlineRename(
    title,
    newTitle => updateConversation(api, convId, { title: newTitle }),
    editingId === convId,
    () => setEditingId(null),
  )
  const isStreaming = useCurrentConversationStore(
    s => s.streamPhase === 'loading' || s.streamPhase === 'streaming',
  )

  return (
    <Link
      to={isStreaming || isActive ? '#' : to}
      onClick={ev => {
        // ev.preventDefault()
        ev.stopPropagation()
        closeSidebarIfMobile()
      }}
      className={cn(isStreaming && 'pointer-events-none opacity-50')}
      aria-label={title}
      tabIndex="0"
    >
      <li className="relative">
        <div
          className={cn(
            classes,
            'relative mx-[6px] flex min-h-9 items-center justify-between gap-2 rounded-md px-2 py-[10px] text-sm',
          )}
        >
          <div
            className={
              'flex min-w-0 grow items-center gap-1 text-black/90 text-sm new-chat:text-white/95 dark:text-white/80'
            }
          >
            <div className="flex min-w-0 grow items-center gap-2">
              <div
                className={cn(
                  'max-w-full',
                  isEditing
                    ? 'no-scrollbar overflow-x-auto whitespace-nowrap'
                    : 'truncate',
                )}
              >
                <span
                  id={`conversation-item-title-${convId}`}
                  {...props}
                  className={cn('whitespace-nowrap focus-visible:outline-0')}
                >
                  {text}
                </span>
              </div>
            </div>
            <ConversationItemDropdown convId={convId} isActive={isActive} />
          </div>
        </div>
      </li>
    </Link>
  )
}
